import * as React from "react";
import * as t from "io-ts";
import { DateFromISOString } from "io-ts-types";
import { sexCodec } from "../../../domain/user";
import { pipe } from "fp-ts/function";
import { array, option, record } from "fp-ts";
import { assertUnreachable } from "../../../utils/assertUnreachable";
import {
    Autocomplete,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { eqString } from "fp-ts/Eq";
import { contramap, ordNumber, ordString } from "fp-ts/Ord";
import { timeToStr } from "../../pages/customer/ResultTable";
import { getLastSemigroup } from "fp-ts/Semigroup";
import { useApi } from "../../../utils/useApi";

interface Props {
    segmentId: string;
}

const Results = (props: Props) => {
    const results = useRes(props.segmentId);
    const [categoryFilter, setCategoryFilter] = React.useState<Array<string>>(
        []
    );

    switch (results.state.type) {
        case "loading":
            return null;

        case "error":
            return null;

        case "completed": {
            const { data } = results.state;
            const categoryNamesById = record.fromFoldableMap(
                getLastSemigroup<string>(),
                array.array
            )(data.categories, (category) => [category.code, category.name]);

            const rows = pipe(
                data.results,
                array.map((row) => ({
                    ...row,
                    category: pipe(
                        categoryNamesById,
                        record.lookup(row.category),
                        option.getOrElse(() => row.category)
                    ),
                }))
            );

            const categories = pipe(
                rows,
                array.map((result) => result.category),
                array.uniq(eqString),
                array.sort(ordString)
            );

            return array.isEmpty(rows) ? null : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h5">Výsledky</Typography>
                        <Autocomplete
                            sx={{
                                minWidth: "10rem",
                            }}
                            multiple
                            options={categories}
                            value={categoryFilter}
                            onChange={(e, value) => {
                                setCategoryFilter(value);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filtr kategorie"
                                />
                            )}
                        />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {pipe(
                                    rows,
                                    array.sort(
                                        contramap(
                                            (result: Result) => result.order
                                        )(ordNumber)
                                    ),
                                    array.filter(
                                        (row) =>
                                            array.isEmpty(categoryFilter) ||
                                            array.elem(eqString)(
                                                row.category,
                                                categoryFilter
                                            )
                                    ),
                                    array.mapWithIndex((i, result) => (
                                        <TableRow key={result.stravaActivityId}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>
                                                {result.name} {result.surname}
                                            </TableCell>
                                            <TableCell>
                                                {timeToStr(result.effort)}
                                            </TableCell>
                                            <TableCell>
                                                {result.effortDate.toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                {result.points}
                                            </TableCell>
                                            <TableCell>
                                                {result.category}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            );
        }
    }

    assertUnreachable(results.state);
};

export default Results;

const useRes = (segmentId: string) => {
    const apiUrl = `https://test.logee.cz:9443/stravaSegments/api/results/${segmentId}`;
    return useApi(
        apiUrl,
        (url) => fetch(url).then((res) => res.json()),
        resultsCodec
    );
};

const categoryCodecCodec = t.string;
const categoryCodec = t.type(
    {
        code: categoryCodecCodec,
        name: t.string,
    },
    "Category"
);

const resultCodec = t.type(
    {
        name: t.string,
        surname: t.string,
        effort: t.number,
        effortDate: DateFromISOString,
        category: categoryCodecCodec,
        order: t.number,
        points: t.number,
        sex: sexCodec,
        stravaActivityId: t.number,
        stravaUserId: t.number,
    },
    "Result"
);
type Result = t.TypeOf<typeof resultCodec>;
const resultsCodec = t.type(
    {
        results: t.array(resultCodec),
        categories: t.array(categoryCodec),
    },
    "Results"
);
